/**
 * pastoralStateCoordinator.js — FASE R3 unified pastoral state layer.
 *
 * Merges: adaptiveMinistryCoordinator (FASE 11) + sustainabilityCoordinator (FASE 12)
 *
 * Answers ONLY: "What is the current human state here,
 * and what kind of pastoral presence does it require?"
 *
 * Does NOT inject: pacing, softness, presence, simplicity.
 * Those are covered by responseStyleEngine, formation, and refinement.
 *
 * Allowed outputs:
 *   exhaustion · compassionate fatigue · pastoral isolation
 *   over-responsibility · emotional capacity limit · escalation/referral
 *
 * Signal gate: omitted when all states are baseline.
 * Max block: 400 chars. No block header.
 */

import { detectPastoralExhaustion }    from "../kevia-sustainability/pastoralExhaustionDetector";
import { assessCompassionateFatigue }  from "../kevia-sustainability/compassionateFatigueEngine";
import { detectPastoralIsolation }     from "../kevia-sustainability/pastoralIsolationAwareness";
import { detectOverresponsibility }    from "../kevia-sustainability/overresponsibilityDetector";
import { evaluateEmotionalCapacity }   from "../kevia-guidance/emotionalCapacityEvaluator";
import { resolveEscalation }           from "../kevia-guidance/escalationDiscernmentEngine";
import { assessMinistryLoad }          from "../kevia-autonomy/ministryLoadBalancer";

const MAX_PASTORAL_STATE_CHARS = 400;

// Compact state observations — name the condition, not the response style
const CAPACITY_LINES = {
  overwhelmed: "Capacidad emocional al límite.",
  depleted:    "Reserva emocional baja.",
  strained:    null,
};

const EXHAUSTION_LINES = {
  "saturación_crítica":     "Saturación ministerial crítica.",
  "desgaste_profundo":      "Desgaste pastoral profundo.",
  "agotamiento_progresivo": null,  // minor; handled by styleEngine when emotion=exhaustion
  "cansancio_temporal":     null,
};

const FATIGUE_LINES = {
  "endurecimiento":          "Posible endurecimiento afectivo — protege la sensibilidad.",
  "desconexión_afectiva":    "Señales de desconexión afectiva.",
  "desgaste_compasivo":      null,
};

const ISOLATION_LINES = {
  "aislamiento_profundo": "Aislamiento ministerial profundo. Menciona red de apoyo.",
  "aislamiento_pastoral": "Señales de aislamiento pastoral.",
};

const RESPONSIBILITY_LINES = {
  "centralización_crítica": "Carga excesiva centralizada. Invita a distribuir.",
  "sobreresponsabilidad":   null,
};

// Only fire for high-stakes escalation (referral / leadership involvement)
const ESCALATION_LINES = {
  "refer_professional":  "Considera sugerir apoyo profesional o de crisis.",
  "involve_leadership":  "La situación puede requerir involucramiento de liderazgo.",
};

const NULL_RESULT = {
  pastoralStateContext:  null,
  exhaustionState:       "estable",
  fatigueType:           "ninguna",
  isolationState:        "acompañado",
  responsibilityState:   "distribuido",
  capacity:              "stable",
  escalation:            "maintain",
  isActive:              false,
};

export function buildPastoralStateContext({
  text               = "",
  intent             = "general",
  emotion            = null,
  riskLevel          = "none",
  processState       = "apertura",
  complexity         = "simple",
  regressionState    = "stable",
  conflictType       = "ninguno",
  congregationSignal = "ninguno",
  loadState          = "low",
  silentTension      = 0,
  familyDynamic      = "ninguno",
  entityContext      = null,
  conversationLength = 0,
}) {
  const sessionLoad  = assessMinistryLoad();
  const ministryLoad = sessionLoad.load;

  const exhaustionResult     = detectPastoralExhaustion({ text, emotion, processState, ministryLoad });
  const fatigueResult        = assessCompassionateFatigue({ text, emotion, exhaustionState: exhaustionResult.state, conversationLength });
  const isolationResult      = detectPastoralIsolation({ text, emotion, exhaustionState: exhaustionResult.state, entityContext });
  const responsibilityResult = detectOverresponsibility({ text, overcentered: loadState === "high", exhaustionState: exhaustionResult.state });
  const capacityResult       = evaluateEmotionalCapacity({ emotion, processState, regressionState, conversationLength, ecclesiaLoad: loadState, silentTension });
  const escalationResult     = resolveEscalation({ riskLevel, conflictType, congregationSignal, loadState, regressionState, processState, complexity, conversationLength });

  // Signal gate
  const hasSignals =
    exhaustionResult.state           !== "estable"    ||
    exhaustionResult.state           !== "cansancio_temporal" && exhaustionResult.state !== "estable" ||
    fatigueResult.fatigueType        !== "ninguna"    ||
    isolationResult.isolationState   !== "acompañado" ||
    responsibilityResult.responsibilityState !== "distribuido" ||
    capacityResult.capacity          !== "stable"     ||
    (escalationResult.escalation === "refer_professional" || escalationResult.escalation === "involve_leadership");

  if (!hasSignals) {
    return {
      ...NULL_RESULT,
      exhaustionState:     exhaustionResult.state,
      fatigueType:         fatigueResult.fatigueType,
      isolationState:      isolationResult.isolationState,
      responsibilityState: responsibilityResult.responsibilityState,
      capacity:            capacityResult.capacity,
      escalation:          escalationResult.escalation,
    };
  }

  // Collect instructions — priority-ordered, compact state observations only
  const lines = [];

  const capacityLine       = CAPACITY_LINES[capacityResult.capacity];
  const exhaustionLine     = EXHAUSTION_LINES[exhaustionResult.state];
  const fatigueLineTxt     = FATIGUE_LINES[fatigueResult.fatigueType];
  const isolationLineTxt   = ISOLATION_LINES[isolationResult.isolationState];
  const responsibilityLine = RESPONSIBILITY_LINES[responsibilityResult.responsibilityState];
  const escalationLine     = ESCALATION_LINES[escalationResult.escalation];

  if (escalationLine)      lines.push(escalationLine);    // safety-adjacent — always first
  if (capacityLine)        lines.push(capacityLine);
  if (exhaustionLine)      lines.push(exhaustionLine);
  if (fatigueLineTxt)      lines.push(fatigueLineTxt);
  if (isolationLineTxt)    lines.push(isolationLineTxt);
  if (responsibilityLine)  lines.push(responsibilityLine);

  if (lines.length === 0) {
    return {
      ...NULL_RESULT,
      exhaustionState:     exhaustionResult.state,
      fatigueType:         fatigueResult.fatigueType,
      isolationState:      isolationResult.isolationState,
      responsibilityState: responsibilityResult.responsibilityState,
      capacity:            capacityResult.capacity,
      escalation:          escalationResult.escalation,
    };
  }

  // No header — plain observations, max 3 lines
  const block = lines.slice(0, 3).join("\n").slice(0, MAX_PASTORAL_STATE_CHARS);

  return {
    pastoralStateContext: block,
    exhaustionState:     exhaustionResult.state,
    fatigueType:         fatigueResult.fatigueType,
    isolationState:      isolationResult.isolationState,
    responsibilityState: responsibilityResult.responsibilityState,
    capacity:            capacityResult.capacity,
    escalation:          escalationResult.escalation,
    isActive:            true,
  };
}
